import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Bulletin } from '../pages/AdminBulletinPage';

export const UrgentBulletinModal: React.FC = () => {
  const { user, isAuthenticated } = useAuth();
  const [urgentList, setUrgentList] = useState<Bulletin[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const authHeaders = useMemo(() => {
    const headers: Record<string, string> = {};
    const token = localStorage.getItem('auth_token');
    if (token) headers['Authorization'] = `Bearer ${token}`;
    return headers;
  }, [user?.id]);

  const checkUrgentBulletins = useCallback(async () => {
    if (!isAuthenticated || !user?.id) return;
    try {
      const res = await fetch('/api/bulletins/urgent-unread', { headers: authHeaders });
      const data = await res.json();
      if (data.success && Array.isArray(data.data) && data.data.length > 0) {
        setUrgentList(data.data);
        setCurrentIndex(0);
        setIsOpen(true);
      } else {
        setUrgentList([]);
        setIsOpen(false);
      }
    } catch (err) {
      console.error('Error checking urgent bulletins:', err);
    }
  }, [isAuthenticated, user?.id, authHeaders]);

  useEffect(() => {
    checkUrgentBulletins();
    // Re-check periodically every 2 minutes
    const timer = setInterval(() => {
      checkUrgentBulletins();
    }, 2 * 60 * 1000);
    return () => clearInterval(timer);
  }, [checkUrgentBulletins]);

  const currentBulletin = urgentList[currentIndex];

  const handleAcknowledgeUrgent = async () => {
    if (!currentBulletin) return;
    try {
      setIsSubmitting(true);
      const res = await fetch(`/api/bulletins/${currentBulletin.id}/read`, {
        method: 'POST',
        headers: {
          ...authHeaders,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ acknowledge: true }),
      });
      const data = await res.json();
      if (data.success) {
        if (currentIndex < urgentList.length - 1) {
          setCurrentIndex(currentIndex + 1);
        } else {
          setIsOpen(false);
          setUrgentList([]);
        }
      }
    } catch (err) {
      console.error('Error acknowledging urgent bulletin:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen || !currentBulletin) return null;

  return (
    <div className="urgent-modal-overlay">
      <div className="urgent-modal-card">
        {/* Pulsing Alert Top Bar */}
        <div className="urgent-banner-bar">
          <div className="pulse-icon">
            <i className="fas fa-exclamation-triangle"></i>
          </div>
          <div>
            <h3 className="urgent-banner-title">THÔNG BÁO KHẨN CẤP</h3>
            <p className="urgent-banner-sub">
              {urgentList.length > 1
                ? `Tin ${currentIndex + 1} trên ${urgentList.length} tin khẩn cần bạn xem`
                : 'Yêu cầu đọc kỹ và xác nhận trước khi tiếp tục'}
            </p>
          </div>
        </div>

        {/* Content Area */}
        <div className="urgent-modal-body">
          <h2 className="urgent-title">{currentBulletin.title}</h2>

          <div className="urgent-meta-line">
            <span>
              <i className="fas fa-user-shield"></i> Người phát hành: <strong>{currentBulletin.author_name}</strong>
            </span>
            <span>
              <i className="fas fa-clock"></i> {new Date(currentBulletin.created_at).toLocaleString('vi-VN')}
            </span>
          </div>

          {currentBulletin.summary && (
            <div className="urgent-summary-callout">
              <i className="fas fa-quote-left" style={{ marginRight: '8px', opacity: 0.6 }}></i>
              {currentBulletin.summary}
            </div>
          )}

          <div className="urgent-text-content">
            {currentBulletin.content.split('\n').map((p, idx) => (
              <p key={idx}>{p || '\u00A0'}</p>
            ))}
          </div>

          {/* Media preview in urgent modal */}
          {currentBulletin.media_files && currentBulletin.media_files.length > 0 && (
            <div className="urgent-media-strip">
              {currentBulletin.media_files.map((m) => (
                <div key={m.fileId} className="urgent-media-item">
                  {m.mimeType.startsWith('image/') ? (
                    <img src={m.url} alt={m.originalName} />
                  ) : (
                    <video controls>
                      <source src={m.url} type={m.mimeType} />
                    </video>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Bottom Action */}
        <div className="urgent-modal-footer">
          <button
            className="btn-urgent-confirm"
            onClick={handleAcknowledgeUrgent}
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <i className="fas fa-spinner fa-spin"></i>
            ) : (
              <i className="fas fa-check-circle" style={{ marginRight: '8px' }}></i>
            )}
            <span>Tôi Đã Đọc, Hiểu Và Xác Nhận Thông Báo Này</span>
          </button>
        </div>
      </div>
    </div>
  );
};
